import { ActionTree, Commit } from 'vuex';
import { State } from '@/store/modules/mq/mqCommonState/mq.store';
import { AxiosResponse } from 'axios';
import graphQl from '@/services/graphQl';
import graphHelpers from '@/utils/graphHelper';
import { Context, Params } from '@/utils/types/global';
import { messageQueueTypes } from '@/components/MQ/mq.types';
import { instanceTypeMapping } from '@/components/MQ/Kafka/kafka.types';

const actions: ActionTree<State, any> = {
  async GET_MQ_SERVICES_WITH_COUNT(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getServiceWithInstanceCount')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.services ?? [];
      });
  },
  async GET_MQ_SERVICES(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('queryServices')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.services ?? [];
      });
  },
  async GET_MQ_INSTANCE(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getServiceInstancesByMetaProperties')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.instanceId ?? [];
      });
  },
  async GET_MQ_CARD_PROPERTIES(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getMQMetricCardProperties')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.values ?? [];
      });
  },
  async GET_KAFKA_CARD_PROPERTIES(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getKafkaMetricsCardProperties')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.values ?? [];
      });
  },
  async GET_MQ_ALERT_INSTANCE(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getMQAlertInstances')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.instanceId ?? [];
      });
  },
  getMQGraphData(context: Context, params: Params) {
    const {
      currentMQService,
      currentBroker,
      currentMQType,
      currentMQSubType,
      activeTab,
    } = context.rootState.mqState.mqCommonState;

    const currentSubType =
      currentMQType.key === messageQueueTypes.kafka
        ? activeTab
        : currentMQSubType.key;

    const config =
      context.rootState.globalState.app.chartsTemplate[currentMQType.key][
        currentSubType
      ][params.layoutIndex][params.index];

    if (!config || !config.metricName || !config.queryMetricType) {
      return;
    }
    const metricNames = graphHelpers.parse(config.metricName);
    const labels = graphHelpers.parse(config.labelsIndex || '');
    const variablesList: Array<any> = metricNames.map((name: string) => {
      let variables = {} as any;
      variables = {
        duration: params.duration,
        condition: {
          name: name,
          entity: {
            scope: 'Service',
            serviceName: currentMQService.key,
            normal: true,
          },
        },
      };
      if (currentMQType.key !== messageQueueTypes.kafka) {
        variables.condition.entity.endpointName = currentBroker.key;
        variables.condition.entity.scope = 'Endpoint';
        variables.labels = labels;
      }
      if (currentMQType.key === messageQueueTypes.kafka) {
        variables.valueColumn = 'value';
        variables.properties = [
          {
            key: 'instanceType',
            value: instanceTypeMapping[activeTab],
          },
        ];
      }
      return variables;
    });

    if (!variablesList || !variablesList.length) {
      return;
    }

    return Promise.all(
      variablesList.map((variable: any) => {
        if (variable) {
          return graphQl
            .graphQuery(variable, config.queryMetricType)
            .then((res: AxiosResponse<any>) => {
              const resData = res.data.data;
              return {
                ...resData,
                config,
                metricName: variable.condition.name,
              };
            });
        } else {
          return { config };
        }
      })
    ).then((data: any) => {
      return data;
    });
  },
  async GET_MQ_PARTICIPANT_INSTANCE(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any }
  ) {
    if (!params) {
      return new Promise<void>((resolve) => resolve());
    }
    return graphQl
      .query('getParticipantInstances')
      .params(params)
      .then((res: AxiosResponse) => {
        return res?.data?.data?.instanceId ?? [];
      });
  },
};

export default actions;
